/**
 * EDİTORYAL ÜRÜN SAYFASI (Lefties / Kith)
 *
 * main-product bölümünde "Düzen: Dikey galeri + sabit panel" seçiliyken
 * yüklenir. Dört parça var:
 *
 *   <pdp-gallery>      Alt alta dizilen fotoğraflar. Masaüstünde sayfayla kayar,
 *                      soldaki küçük önizlemeler görünen fotoğrafı izler ve
 *                      tıklanınca oraya götürür. Mobilde fotoğraflar kendi
 *                      kutusunda kayar; kutunun yüksekliği ad, fiyat, renkler ve
 *                      EKLE ilk ekrana sığacak şekilde hesaplanır (Lefties).
 *   <pdp-size-picker>  EKLE'ye basınca açılan beden listesi. Beden seçilince o
 *                      varyant forma yazılır ve temanın product-form'u sepete
 *                      ekleyip sepet panelini açar.
 *   <pdp-tabs>         Detaylar / Beden ve Kalıp / Gönderi ve İadeler (Kith).
 *   Beden rehberi      [data-size-guide-open] ile açılan <dialog>.
 *
 * Tema, varyant her değiştiğinde bölümün HTML'ini sunucudan çekip yalnızca
 * belirli parçaları (fiyat, SKU, stok...) yeniliyor. Ref/renk satırı ve EKLE
 * yazısı o listede yok; aşağıdaki variant-change aboneliği ikisini de tazeler.
 */
(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 749px)';

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

  /** Sticky header'ın kapladığı yükseklik + küçük bir pay. */
  const headerOffset = () => {
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    return (Number.isFinite(header) ? header : 0) + 16;
  };

  const parseJSON = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  };

  /* ------------------------------------------------------------------------
     Galeri
     ------------------------------------------------------------------------ */

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.viewport = this.querySelector('[data-pdp-gallery-viewport]');
      this.items = Array.from(this.querySelectorAll('.pdp-gallery__item'));
      this.thumbRail = this.querySelector('.pdp-gallery__thumbs');
      this.thumbs = Array.from(this.querySelectorAll('.pdp-gallery__thumb'));
      this.bullets = Array.from(this.querySelectorAll('.pdp-gallery__bullet'));
      this.activeIndex = 0;
      this.ratios = new Map();
      this.lastWidth = window.innerWidth;

      this.onThumbClick = this.onThumbClick.bind(this);
      this.onViewportScroll = this.onViewportScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onQueryChange = this.onQueryChange.bind(this);
      this.onWindowLoad = () => this.fitMobileHeight();

      this.thumbRail?.addEventListener('click', this.onThumbClick);
      this.viewport?.addEventListener('scroll', this.onViewportScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('load', this.onWindowLoad, { once: true });
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.mobileQuery.addEventListener?.('change', this.onQueryChange);

      this.observe();
      this.fitMobileHeight();
      this.watchPanel();
      this.watchVariants();
      this.initZoom();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.panelObserver?.disconnect();
      this.thumbRail?.removeEventListener('click', this.onThumbClick);
      this.viewport?.removeEventListener('scroll', this.onViewportScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('load', this.onWindowLoad);
      this.mobileQuery?.removeEventListener?.('change', this.onQueryChange);
      this.unsubscribeVariant?.();
      this.lightbox?.destroy?.();
    }

    onQueryChange() {
      this.observe();
      this.fitMobileHeight();
    }

    /**
     * En çok görünen fotoğraf "aktif" sayılır. Mobilde kayan şey galeri
     * kutusu olduğu için kök o; masaüstünde sayfanın kendisi.
     */
    observe() {
      this.observer?.disconnect();
      this.ratios.clear();
      if (this.items.length < 2 || !('IntersectionObserver' in window)) return;

      const mobile = isMobile();
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio));

          let best = null;
          let bestRatio = 0;
          this.items.forEach((item) => {
            const ratio = this.ratios.get(item) || 0;
            if (ratio > bestRatio + 0.001) {
              best = item;
              bestRatio = ratio;
            }
          });
          if (best) this.setActive(Number(best.dataset.index));
        },
        {
          root: mobile ? this.viewport : null,
          rootMargin: mobile ? '0px' : `-${Math.round(headerOffset())}px 0px 0px 0px`,
          threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
        }
      );
      this.items.forEach((item) => this.observer.observe(item));
    }

    setActive(index) {
      if (index === this.activeIndex && this.dataset.activeSynced === 'true') return;
      this.activeIndex = index;
      this.dataset.activeSynced = 'true';

      this.thumbs.forEach((thumb, i) => {
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      this.bullets.forEach((bullet, i) => bullet.classList.toggle('is-active', i === index));
      this.keepThumbVisible(index);
    }

    /** Önizleme şeridi ekrana sığmıyorsa aktif önizlemeyi görünür tutar. */
    keepThumbVisible(index) {
      const rail = this.thumbRail;
      const thumb = this.thumbs[index];
      if (!rail || !thumb || rail.scrollHeight <= rail.clientHeight + 1) return;

      const top = thumb.offsetTop;
      const bottom = top + thumb.offsetHeight;
      if (top < rail.scrollTop) {
        rail.scrollTo({ top: Math.max(0, top - 8), behavior: scrollBehavior() });
      } else if (bottom > rail.scrollTop + rail.clientHeight) {
        rail.scrollTo({ top: bottom - rail.clientHeight + 8, behavior: scrollBehavior() });
      }
    }

    onThumbClick(event) {
      const thumb = event.target.closest('.pdp-gallery__thumb');
      if (!thumb) return;
      this.scrollToIndex(Number(thumb.dataset.index));
    }

    scrollToIndex(index) {
      const item = this.items[index];
      if (!item) return;

      if (isMobile() && this.viewport && this.viewport.scrollHeight > this.viewport.clientHeight) {
        this.viewport.scrollTo({ top: item.offsetTop, behavior: scrollBehavior() });
      } else {
        const top = item.getBoundingClientRect().top + window.scrollY - headerOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior() });
      }
      this.setActive(index);
    }

    /** Lefties'teki gibi: kaydırırken soldaki çizgiler belirir, durunca söner. */
    onViewportScroll() {
      if (!isMobile()) return;
      this.classList.add('is-scrolling');
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.classList.remove('is-scrolling'), 900);
    }

    onResize() {
      // Mobilde adres çubuğu açılıp kapanınca yükseklik değişir; o durumda
      // kutuyu yeniden boyutlamak kaydırma sırasında zıplamaya yol açar.
      // Yalnızca genişlik (yön) değişince yeniden hesapla.
      if (window.innerWidth === this.lastWidth) return;
      this.lastWidth = window.innerWidth;
      this.fitMobileHeight();
    }

    /**
     * Mobil galeri kutusunun yüksekliği: ilk ekranın kalanı, eksi kutunun
     * altında EKLE'nin bitimine kadar olan bölüm. Böylece ad, fiyat, renkler
     * ve EKLE sayfa kaydırılmadan görünür. Kare ile fotoğrafın kendi oranı
     * arasında tutulur: çok kısa ekranda fotoğraf ezilmez, uzun ekranda da
     * fotoğrafın doğal yüksekliğini aşıp kırpılmaz.
     */
    fitMobileHeight() {
      if (!this.viewport) return;
      if (!isMobile() || this.items.length < 2) {
        this.style.removeProperty('--pdp-gallery-h');
        return;
      }

      const button = this.closest('.product--editorial')?.querySelector('.product-form__submit');
      if (!button) return;

      const box = this.viewport.getBoundingClientRect();
      const boxTop = box.top + window.scrollY;
      const buttonBottom = button.getBoundingClientRect().bottom + window.scrollY;
      const below = buttonBottom - (boxTop + box.height) + 16;

      const ratio = parseFloat(getComputedStyle(this).getPropertyValue('--pdp-media-ratio')) || 2 / 3;
      const natural = box.width / ratio;
      const target = window.innerHeight - boxTop - below;
      const height = Math.round(Math.max(box.width, Math.min(natural, target)));

      if (Number.isFinite(height) && height > 0) {
        this.style.setProperty('--pdp-gallery-h', `${height}px`);
      }
    }

    /**
     * Panel yüksekliği CSS'e aktarılır: panel ekrandan uzunsa (açık sekme
     * uzun metin içeriyorsa) sticky "üstten" değil "alttan" tutunur, böylece
     * panelin sonu hiçbir zaman ekranın dışında kalmaz.
     * Aynı gözlemci mobilde renkler geç yüklenip satır uzarsa galeriyi de
     * yeniden boyutlar (kullanıcı henüz kaydırmadıysa).
     */
    watchPanel() {
      const panel = this.closest('.product--editorial')?.querySelector('.product-grid__item--content-wrapper');
      if (!panel || !('ResizeObserver' in window)) return;

      this.panelObserver = new ResizeObserver(() => {
        panel.style.setProperty('--pdp-panel-h', `${Math.ceil(panel.offsetHeight)}px`);
        if (window.scrollY < 8) this.fitMobileHeight();
      });
      this.panelObserver.observe(panel);
    }

    /** Aynı ürünün başka rengi/bedeni seçilince o varyantın fotoğrafına gider. */
    watchVariants() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
        const data = payload && payload.data;
        if (!data || data.sectionId !== this.dataset.section) return;

        const mediaId = data.variant && data.variant.featured_media && data.variant.featured_media.id;
        if (!mediaId) return;

        const index = this.items.findIndex((item) => Number(item.dataset.mediaId) === mediaId);
        if (index < 0 || index === this.activeIndex) return;

        if (isMobile()) {
          this.scrollToIndex(index);
          return;
        }
        // Masaüstünde yalnızca fotoğraf ekranda değilse sayfayı kaydır.
        const rect = this.items[index].getBoundingClientRect();
        if (rect.bottom < headerOffset() || rect.top > window.innerHeight) this.scrollToIndex(index);
      });
    }

    /** Fotoğrafa tıklayınca büyütme (temanın PhotoSwipe'ı). */
    initZoom() {
      if (!this.hasAttribute('data-zoom')) return;

      const start = () => {
        if (this.lightbox || typeof PhotoSwipeLightbox === 'undefined' || typeof PhotoSwipe === 'undefined') return;

        const icons = this.querySelector('template[data-pdp-zoom-icons]');
        const icon = (name) => icons?.content.querySelector(`[data-icon="${name}"]`)?.innerHTML || '';

        this.lightbox = new PhotoSwipeLightbox({
          gallery: this.querySelector('.pdp-gallery__list'),
          children: 'a.pdp-gallery__zoom',
          pswpModule: PhotoSwipe,
          mainClass: 'pswp--product-media-gallery',
          showHideAnimationType: 'zoom',
          loop: false,
          initialZoomLevel: (zoom) => (isMobile() ? zoom.vFill : zoom.fit),
          secondaryZoomLevel: (zoom) => (isMobile() ? zoom.fit : 1),
        });

        this.lightbox.addFilter('uiElement', (element, data) => {
          if (data.name === 'close') element.innerHTML = icon('close');
          if (data.name === 'arrowPrev') element.innerHTML = icon('prev');
          if (data.name === 'arrowNext') element.innerHTML = icon('next');
          return element;
        });
        this.lightbox.on('beforeOpen', () => document.body.classList.add('overflow-hidden'));
        this.lightbox.on('closingAnimationStart', () => document.body.classList.remove('overflow-hidden'));
        this.lightbox.init();
      };

      // PhotoSwipe dosyaları bu dosyadan sonra yükleniyor (hepsi defer).
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
      } else {
        start();
      }
      window.addEventListener('load', start, { once: true });
    }
  }

  if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);

  /* ------------------------------------------------------------------------
     Beden listesi (Lefties)
     ------------------------------------------------------------------------ */

  class PdpSizePicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.sectionId = this.dataset.section;
      this.formId = this.dataset.formId;
      this.position = Number(this.dataset.sizePosition) || 0;
      this.panel = this.querySelector('.pdp-sizes__panel');
      this.list = this.querySelector('.pdp-sizes__list');

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOutsidePointer = this.onOutsidePointer.bind(this);

      this.addEventListener('click', this.onClick);
      // Yakalama aşamasında: form gönderilmeden önce devreye girer.
      document.addEventListener('click', this.onTriggerClick, true);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('click', this.onTriggerClick, true);
      this.teardownOpenListeners();
    }

    get form() {
      return document.getElementById(this.formId);
    }

    get isOpen() {
      return this.opened === true;
    }

    /** EKLE ve (açıksa) alttaki sabit sepete ekle çubuğunun butonu. */
    onTriggerClick(event) {
      const trigger = event.target.closest(
        `#ProductSubmitButton-${this.sectionId}, .product-bar__submit[form="${this.formId}"]`
      );
      if (!trigger || trigger.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      if (this.isOpen) {
        this.close();
        return;
      }

      // Alttaki sabit çubuktan gelindiyse: masaüstünde liste EKLE'nin yerinde
      // açıldığı için önce EKLE ekrana getirilir (mobilde liste zaten alttan açılır).
      const mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (trigger !== mainButton && mainButton && !isMobile()) {
        mainButton.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
        this.open(mainButton);
        return;
      }
      this.open(trigger);
    }

    open(trigger) {
      this.refresh();
      this.trigger = trigger;
      this.opened = true;
      clearTimeout(this.hideTimer);
      this.hidden = false;
      // Görünür olduktan sonraki karede sınıf eklenir ki geçiş çalışsın.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (this.opened) this.classList.add('is-open');
        })
      );
      this.setExpanded(true);

      if (isMobile()) document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('pointerdown', this.onOutsidePointer, true);

      const first = this.list?.querySelector('.pdp-sizes__option:not([aria-disabled="true"])');
      (first || this.panel)?.focus({ preventScroll: true });
    }

    close({ returnFocus = true } = {}) {
      if (!this.isOpen) return;
      this.opened = false;
      this.classList.remove('is-open');
      this.setExpanded(false);
      this.teardownOpenListeners();
      document.body.classList.remove('overflow-hidden');

      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        if (!this.opened) this.hidden = true;
      }, prefersReducedMotion() ? 0 : 260);

      if (returnFocus) this.trigger?.focus({ preventScroll: true });
    }

    teardownOpenListeners() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('pointerdown', this.onOutsidePointer, true);
    }

    setExpanded(expanded) {
      document
        .getElementById(`ProductSubmitButton-${this.sectionId}`)
        ?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    onClick(event) {
      if (event.target.closest('[data-pdp-sizes-close]')) {
        this.close();
        return;
      }
      const option = event.target.closest('.pdp-sizes__option');
      if (!option || option.getAttribute('aria-disabled') === 'true') return;
      this.select(option);
    }

    /** Masaüstünde listenin dışına tıklanınca kapanır. */
    onOutsidePointer(event) {
      const target = event.target;
      if (this.panel && this.panel.contains(target)) return;
      if (this.trigger && this.trigger.contains(target)) return; // butonun kendi tıklaması açar/kapar
      if (target.closest && target.closest('dialog[open]')) return; // beden rehberi üstte açıkken
      this.close({ returnFocus: false });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (document.querySelector('dialog.pdp-size-guide[open]')) return; // önce rehber kapanır
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const options = Array.from(this.list?.querySelectorAll('.pdp-sizes__option') || []);
      const index = options.indexOf(document.activeElement);
      if (index < 0) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      options[(index + step + options.length) % options.length].focus();
    }

    /**
     * Satırları güncel varyant verisinden yeniden hesaplar. Tema varyant
     * seçicisini her değişimde yeniden çizdiği için JSON her zaman günceldir.
     */
    refresh() {
      if (!this.list || !this.position) return;
      const selects = document.getElementById(`product-variant-selects-${this.sectionId}`);
      const variants = parseJSON(selects && selects.querySelector('[data-all-variants]'));
      if (!Array.isArray(variants)) return;
      const selected = parseJSON(selects.querySelector('[data-selected-variant]'));
      const key = `option${this.position}`;

      this.list.querySelectorAll('.pdp-sizes__option').forEach((option) => {
        const value = option.dataset.sizeValue;
        const match = variants.find(
          (variant) =>
            variant[key] === value &&
            [1, 2, 3].every(
              (n) => n === this.position || !selected || variant[`option${n}`] === selected[`option${n}`]
            )
        );
        const available = Boolean(match && match.available);

        option.dataset.variantId = match ? match.id : '';
        if (available) {
          option.removeAttribute('aria-disabled');
        } else {
          option.setAttribute('aria-disabled', 'true');
        }
        const status = option.querySelector('.pdp-sizes__status');
        if (status) status.hidden = available;
      });
    }

    select(option) {
      const form = this.form;
      const variantId = option.dataset.variantId;
      const idInput = form && form.querySelector('input[name="id"]');
      if (!idInput || !variantId) return;

      idInput.value = variantId;
      idInput.disabled = false;
      this.syncNativeInput(option.dataset.sizeValue);

      // Odak EKLE'ye döner: sepet paneli kapanınca odağı oraya geri verir.
      this.close({ returnFocus: true });

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }

    /** Gizli beden seçicisini de aynı bedene getirir (olay göndermeden). */
    syncNativeInput(value) {
      const selects = document.getElementById(`product-variant-selects-${this.sectionId}`);
      if (!selects) return;
      selects.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input.name.trim().endsWith(`-${this.position}`) && input.value === value) input.checked = true;
      });
    }
  }

  if (!customElements.get('pdp-size-picker')) customElements.define('pdp-size-picker', PdpSizePicker);

  /* ------------------------------------------------------------------------
     Sekmeler (Kith)
     ------------------------------------------------------------------------ */

  class PdpTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    onClick(event) {
      const tab = event.target.closest('[role="tab"]');
      if (tab && this.contains(tab)) this.select(tab);
    }

    onKeydown(event) {
      const index = this.tabs.indexOf(document.activeElement);
      if (index < 0) return;

      let next = null;
      if (event.key === 'ArrowRight') next = this.tabs[(index + 1) % this.tabs.length];
      if (event.key === 'ArrowLeft') next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
      if (event.key === 'Home') next = this.tabs[0];
      if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
      if (!next) return;

      event.preventDefault();
      this.select(next);
      next.focus();
    }

    select(tab) {
      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(item.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    }
  }

  if (!customElements.get('pdp-tabs')) customElements.define('pdp-tabs', PdpTabs);

  /* ------------------------------------------------------------------------
     Beden rehberi penceresi
     ------------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-size-guide-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
        event.preventDefault();
        dialog.showModal();
        document.body.classList.add('overflow-hidden');
      }
      return;
    }

    const dialog = event.target.closest('dialog.pdp-size-guide');
    if (!dialog) return;
    // Kapatma butonu ya da pencerenin dışındaki karartılmış alan.
    if (event.target.closest('[data-size-guide-close]') || event.target === dialog) dialog.close();
  });

  document.addEventListener(
    'close',
    (event) => {
      if (!event.target.matches || !event.target.matches('dialog.pdp-size-guide')) return;
      // Beden listesi mobilde açıkken sayfa kilidi onda kalsın.
      const sheetOpen = document.querySelector('pdp-size-picker.is-open');
      if (!(sheetOpen && isMobile())) document.body.classList.remove('overflow-hidden');
    },
    true
  );

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.matches || !input.matches('.pdp-size-guide__unit input')) return;
    input.closest('dialog.pdp-size-guide')?.setAttribute('data-unit', input.value);
  });

  /* ------------------------------------------------------------------------
     Varyant değişimi: Ref / renk satırı ve EKLE yazısı
     ------------------------------------------------------------------------ */

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      const data = payload && payload.data;
      if (!data || !data.sectionId) return;

      const current = document.getElementById(`PdpReference-${data.sectionId}`);
      const fresh = data.html && data.html.getElementById && data.html.getElementById(`PdpReference-${data.sectionId}`);
      if (current && fresh) {
        current.innerHTML = fresh.innerHTML;
        current.hidden = fresh.hidden;
      }

      // Tema butonu her değişimde "Sepete ekle"ye döndürüyor; bu düzende "Ekle".
      const button = document.getElementById(`ProductSubmitButton-${data.sectionId}`);
      const label = button && button.dataset.labelAdd;
      const text = button && button.querySelector('span');
      const themeLabel = window.variantStrings && window.variantStrings.addToCart;
      if (label && text && !button.disabled && text.textContent.trim() === String(themeLabel || '').trim()) {
        text.textContent = label;
      }
    });
  }
})();
